import { AgreementKind, EvidenceStatus, RunResultsQuery, SuppliedResultRow } from '../../../../services/benchmarkService';
import { TIERS_BY_URGENCY, humanise, layerName, outcomeLabel } from '../../BenchmarkVocabulary';

/**
 * Vocabulary for a supplied run's results: how an alert's own tier compares with
 * the platform's, and what each scoring layer and agent found.
 */

export interface AgreementMeta {
    label: string;
    color: string;
    /** What the badge means, for its tooltip. */
    description: string;
}

/** In the order the filter chips list them. */
export const AGREEMENT_ORDER: AgreementKind[] = [
    'agree', 'platform_higher', 'platform_lower', 'no_comparable_tier', 'not_scored'
];

export const AGREEMENT_META: Record<AgreementKind, AgreementMeta> = {
    agree: {
        label: 'Agrees',
        color: '#10B981',
        description: 'The platform gave this alert the same tier as you did.'
    },
    platform_higher: {
        label: 'Platform higher',
        color: '#F97316',
        description: 'The platform ranked this alert more urgent than you did.'
    },
    platform_lower: {
        label: 'Platform lower',
        color: '#0EA5E9',
        description: 'The platform ranked this alert less urgent than you did.'
    },
    no_comparable_tier: {
        label: 'Not comparable',
        color: '#94A3B8',
        description: "Your tier for this alert has no approved mapping onto the platform's scale, so the two cannot be compared."
    },
    not_scored: {
        label: 'Not scored',
        color: '#64748B',
        description: 'This alert was supplied but not scored — the sample was larger than the run allows and was cut down.'
    }
};

export const agreementMeta = (kind: string): AgreementMeta =>
    AGREEMENT_META[kind as AgreementKind] ?? { label: humanise(kind), color: '#94A3B8', description: '' };

/**
 * An alert's outcome as the user's file recorded it. Canonical true/false
 * positives use the dashboard's wording; anything else is named as it came.
 */
export const resultOutcomeLabel = (disposition: string | null | undefined): string => {
    if (!disposition) return '—';
    if (disposition === 'true_positive' || disposition === 'false_positive') return outcomeLabel(disposition);
    return humanise(disposition);
};

/**
 * Filter combinations worth one click: the disagreements that matter most to a
 * team validating the platform against its own judgement.
 */
export const RESULT_PRESETS: { key: string; label: string; filters: Pick<RunResultsQuery, 'agreement' | 'outcome'> }[] = [
    {
        key: 'tp_lower',
        label: 'True positives the platform ranked lower than you',
        filters: { agreement: 'platform_lower', outcome: 'true_positive' }
    },
    {
        key: 'fp_higher',
        label: 'False positives it ranked higher',
        filters: { agreement: 'platform_higher', outcome: 'false_positive' }
    }
];

/* ------------------------------------------------------------------ *
 * Scoring layers and evidence
 * ------------------------------------------------------------------ */

/** The score's layers, in scoring order. */
export const SCORE_LAYERS = [
    'layer1_base_signals',
    'layer2_customer_risk',
    'layer3_network_amplifiers',
    'layer4_recency_velocity'
] as const;

export const layerLabel = (key: string): string => layerName(key);

const AGENT_NAMES: Record<string, string> = {
    INVESTIGATE_ACCOUNT_TYPES: 'Account types',
    INVESTIGATE_ENTITY_NETWORK: 'Entity network',
    INVESTIGATE_TIMING_INTERVALS: 'Timing between transactions',
    INVESTIGATE_TXN_AMOUNTS_DISTRIBUTION: 'Transaction amounts',
    INVESTIGATE_TRANSACTION_ANOMALIES: 'Transaction anomalies',
    SCREEN_FOR_RESTRICTED_PARTY: 'Restricted-party screening',
    SEARCH_ADVERSE_MEDIA: 'Adverse media search'
};

export const agentLabel = (code: string): string => AGENT_NAMES[code] ?? humanise(code);

export interface EvidenceMeta {
    label: string;
    color: string;
    /**
     * True when the layer or agent had evidence to score, false when it had
     * none. Null when the check itself failed, which says nothing either way.
     */
    present: boolean | null;
}

/**
 * What a layer's or agent's status means for the reader.
 *
 * `not_found` and `skipped` are NO EVIDENCE, not negative evidence: the signal
 * was absent and scored zero. They are shown in a neutral grey and never in a
 * colour that reads as a finding against the customer.
 */
export function evidenceMeta(status: EvidenceStatus | null | undefined): EvidenceMeta {
    switch (status) {
        case 'found':
            return { label: 'Evidence found', color: '#10B981', present: true };
        case 'context_derived':
            return { label: "Evidence found (from the alert's context)", color: '#10B981', present: true };
        case 'kyc_derived':
            return { label: 'Evidence found (from KYC)', color: '#10B981', present: true };
        case 'not_found':
            return { label: 'No evidence', color: '#94A3B8', present: false };
        case 'skipped':
            return { label: 'No evidence (not checked)', color: '#94A3B8', present: false };
        case 'error':
            return { label: 'Check failed', color: '#FBBF24', present: null };
        default:
            return { label: status ? humanise(String(status)) : 'Not recorded', color: '#64748B', present: null };
    }
}

/* ------------------------------------------------------------------ *
 * Tables
 * ------------------------------------------------------------------ */

/**
 * Tier codes for a matrix axis: the platform's scale, most urgent first, plus
 * any other code the data carries, so nothing in the run is silently dropped.
 */
export function tierAxis(present: Iterable<string | null | undefined>): string[] {
    const extra = new Set<string>();
    for (const code of present) {
        if (code && !(TIERS_BY_URGENCY as string[]).includes(code)) extra.add(code);
    }
    return [...TIERS_BY_URGENCY, ...[...extra].sort().reverse()];
}

export interface CaseGroup {
    caseRef: string;
    alertsInCase: number;
    rows: SuppliedResultRow[];
}

/**
 * Consecutive rows of one page grouped by case. The API orders by case, so a
 * case's alerts sit together; a group may still be split across pages.
 */
export function groupByCase(rows: SuppliedResultRow[]): CaseGroup[] {
    const groups: CaseGroup[] = [];
    for (const row of rows) {
        const last = groups[groups.length - 1];
        if (last && last.caseRef === row.caseRef) {
            last.rows.push(row);
        } else {
            groups.push({ caseRef: row.caseRef, alertsInCase: row.alertsInCase, rows: [row] });
        }
    }
    return groups;
}
